// @flow
import * as React from 'react';
import styled from 'styled-components';
import interpolateComponents from 'interpolate-components';
import RadioButtonsField from 'components/Form/RadioButtonsField';
import Text, { BodySmall } from 'components/Text';
import Box from 'components/Box';
import Link from 'components/Link';
import Button, { LinkButton } from 'components/Button';
import Icon from 'components/Icon';
import { defaultButtonStyles } from 'components/Tooltip';
import Img from 'components/Image';
import { trackClicked } from 'utils/analytics';
import { isDefined } from 'utils/functional/type';
import { colors, transition, gutterWidth } from 'theme';

const { useState } = React;

const Image = styled.div`
  img {
    width: calc(100vw - 48px);
    max-width: 1024px;
    @media (min-aspect-ratio: 2048 / 1260) {
      max-height: 620px;
      width: auto;
      height: calc(100vh - 48px);
    }
  }
`;

const PreviewOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: all ${transition} 0.1s;
`;

const PreviewPane = styled(Box)`
  position: fixed;
  z-index: 1001;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 24px;
  border-radius: 24px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all ${transition} 0.1s;
  pointer-events: none;
`;

const PreviewLink = styled(Box)`
  display: inline-block;
  background-image: linear-gradient(
    to bottom,
    ${colors.waterBlue} 0%,
    ${colors.waterBlue} 100%
  );
  background-position: 0 1.2em;
  background-repeat: repeat-x;
  background-size: 100% 0.05em;
`;

const PreviewButtonIcon = styled(Box)`
  ${defaultButtonStyles};
  transform: translateY(-2px);
  display: inline-block;
  margin-left: 4px;
`;

const PreviewButton = styled(Button)`
  display: inline-block;
  &
    ~ ${/* sc-selector */ PreviewPane},
    &
    ~ ${/* sc-selector */ PreviewOverlay} {
    opacity: 0;
    visibility: hidden;
  }
  & ~ ${/* sc-selector */ PreviewPane} {
    transform: translate(-50%, -50%) translateZ(0) scale(0.95);
    opacity: 0;
    visibility: hidden;
  }
  &:hover,
  &:focus {
    &
      ~ ${/* sc-selector */ PreviewPane},
      &
      ~ ${/* sc-selector */ PreviewOverlay} {
      transform: scale(1) translateZ(0);
      visibility: visible;
      opacity: 1;
    }
    & ~ ${/* sc-selector */ PreviewPane} {
      transform: translate(-50%, -50%) translateZ(0) scale(1);
    }
  }
`;

export const ScreenshotTooltip = (props?: Object) => {
  const onKeyUp = e => {
    if (e.key === 'Escape') {
      e.target.blur();
    }
  };
  return (
    <Box display="inline-block">
      <PreviewButton onKeyUp={onKeyUp} data-skip-tab="true">
        <PreviewLink {...props} />
        <PreviewButtonIcon />
      </PreviewButton>
      <PreviewOverlay />
      <PreviewPane>
        <Image>
          <Img
            src="/static/images/ats-preview.jpg"
            alt={getText('Preview of Candidate Dashboard')}
          />
        </Image>
      </PreviewPane>
    </Box>
  );
};

export const AtsDescription = () => (
  <>
    {interpolateComponents({
      mixedString: getText(
        `Our new Applicant Tracker lets you collect resumes and cover letters 
        in one {{tooltip}}easy-to-use dashboard{{/tooltip}}, send auto-responses 
        to your applicants, download and share applications, assign candidates to 
        various hiring stages, and see the number of views on your listings, 
        {{strong}}all for free!{{/strong}} {{link}}Learn More {{icon/}}{{/link}}`
      ),
      components: {
        strong: <Text tag="strong" mr="4px" />,
        tooltip: <ScreenshotTooltip />,
        link: (
          <Box
            component={Link}
            display="inline-block"
            target="_blank"
            href="/applicant-tracker"
          />
        ),
        icon: (
          <Box display="inline-block" ml="3px">
            <Icon
              name="external-link"
              width={12}
              height={12}
              stroke="currentColor"
            />
          </Box>
        )
      }
    })}
  </>
);

const EnableAtsField = ({
  hasAts,
  orgId,
  listingType
}: {
  hasAts: boolean,
  orgId: string,
  listingType: string
}) => {
  const [expanded, setExpanded] = useState(!isDefined(hasAts));

  return expanded ? (
    <RadioButtonsField
      label={getText('Application Settings')}
      description={
        <BodySmall mb={24} lineHeight={1.375}>
          <AtsDescription />
        </BodySmall>
      }
      columns={2}
      large
      name="hasAts"
      buttons={[
        {
          label: (
            <Text fontSize={18}>
              {getText(`Yes, use Idealist to track applicants`)}
            </Text>
          ),
          value: true
        },
        {
          label: (
            <Text fontSize={18}>{getText(`No, I’ll use another method`)}</Text>
          ),
          value: false
        }
      ]}
      onUpdateValue={value =>
        trackClicked('Enable ATS - Admin', {
          enable: value,
          org_id: orgId,
          listing_type: listingType
        })
      }
    />
  ) : (
    <Box
      px={gutterWidth / 2}
      py={gutterWidth / 3}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={48}
      borderRadius="8px"
      bg={colors.platinum}
    >
      <Box flex="0 0 auto">
        <Text fontSize={18} color={colors.middleGrey}>
          {hasAts
            ? getText(`You're using Idealist to track applicants`)
            : interpolateComponents({
                mixedString: getText(
                  `You're {{u}}not{{/u}} using Idealist to track applicants`
                ),
                components: { u: <u /> }
              })}
        </Text>
      </Box>
      <Box flex="0 0 auto">
        <LinkButton
          data-qa-id="change-ats-settings-button"
          onClick={() => setExpanded(true)}
        >
          {getText('Change Settings')}
        </LinkButton>
      </Box>
    </Box>
  );
};

export default EnableAtsField;
